import React, {
  FC,
  Fragment,
  ReactNode,
  RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import styled, {StyledComponent} from 'styled-components';

import {FrontEvent, Handler, HandlerOf, Renderer} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';

/*
 * Props + Types.
 */

interface FolderBreadcrumbsProps {
  topLevelBreadcrumb: FolderBreadcrumb;
  folderPathBreadcrumbs: ReadonlyArray<FolderBreadcrumb>;
  styles?: FolderBreadcrumbStyleOverride;
}

type StyledSpan<T extends Object = {}> = StyledComponent<'span', any, T, never>;

interface FolderBreadcrumbStyleOverride {
  ancestorSpan?: StyledSpan<StyledAncestorFolderBreadcrumbStyleProps>;
  currentFolderSpan?: StyledSpan;
  breadcrumbSeparator?: ReactNode;
  showSeparatorAfterTopLevel?: boolean;
}

export interface FolderBreadcrumb {
  label: ReactNode;
  onBreadcrumbClick: HandlerOf<FrontEvent | undefined>;
}

/*
 * Styles.
 */

const StyledBreadcrumbsSpan = styled.span`
  display: flex;
`;

const StyledCurrentFolderBreadcrumbSpan = styled.span`
  color: ${(p) => p.theme.greys.shade80};
`;

export interface StyledAncestorFolderBreadcrumbStyleProps {
  $isSolitaryTopLevelBreadcrumb?: boolean;
}

const StyledAncestorFolderBreadcrumbSpan = styled.span<StyledAncestorFolderBreadcrumbStyleProps>`
  min-width: fit-content;
  color: ${(p) => p.theme.greys.shade60};

  &:hover {
    color: ${(p) => (p.$isSolitaryTopLevelBreadcrumb ? p.theme.greys.shade60 : p.theme.greys.shade70)};
  }
`;

const StyledEllipsisTooltipCoordinator = styled(TooltipCoordinator)`
  min-width: auto;
  align-self: center;
`;

const StyledEllpsisBreadcrumbSpan = styled.span`
  color: ${(p) => p.theme.greys.shade60};
  &:hover {
    color: ${(p) => p.theme.greys.shade70};
  }
`;

const StyledChevronIcon = styled(Icon)`
  display: inline-block;
  vertical-align: text-bottom;
  margin: 0px 2px 1px;
`;

/*
 * Component.
 */

export const FolderBreadcrumbs: FC<FolderBreadcrumbsProps> = (props) => {
  const {folderPathBreadcrumbs, topLevelBreadcrumb, styles} = props;

  const [numBreadcrumbsToRender, setNumBreadcrumbsToRender] = useState<number>(folderPathBreadcrumbs.length);
  const currentFolderRef = useRef<HTMLDivElement>(null);

  // Reset the number of breadcrumbs to render to the max (to start) if we ever switch folders.
  useLayoutEffect(() => setNumBreadcrumbsToRender(folderPathBreadcrumbs.length), [folderPathBreadcrumbs]);

  // This will recursively subtract 1 from the number of breadcrumbs to render
  // until we are rendering the highest number of breadcrumbs we can without ellipsising.
  useLayoutEffect(() => {
    if (numBreadcrumbsToRender <= 1) {
      return;
    }

    if (currentFolderRef.current && isElementOverflowing(currentFolderRef.current)) {
      setNumBreadcrumbsToRender((prevNumBreadcrumbsToRender) => prevNumBreadcrumbsToRender - 1);
    }
  }, [setNumBreadcrumbsToRender, numBreadcrumbsToRender]);

  const renderAllBreadcrumbsTooltipCallback = useCallback(
    () => renderAllBreadcrumbsTooltip(topLevelBreadcrumb, folderPathBreadcrumbs),
    [folderPathBreadcrumbs, topLevelBreadcrumb],
  );

  return (
    <StyledBreadcrumbsSpan>
      {renderBreadcrumbs(
        folderPathBreadcrumbs,
        topLevelBreadcrumb,
        numBreadcrumbsToRender,
        currentFolderRef,
        renderAllBreadcrumbsTooltipCallback,
        styles,
      )}
    </StyledBreadcrumbsSpan>
  );
};

/*
 * Helpers.
 */

function renderAncestorBreadcrumb(
  breadcrumb: FolderBreadcrumb,
  key: string,
  style?: FolderBreadcrumbStyleOverride,
) {
  const StyledAncestorSpan = style?.ancestorSpan || StyledAncestorFolderBreadcrumbSpan;

  return (
    <StyledAncestorSpan key={`SPAN:${key}`} onClick={breadcrumb.onBreadcrumbClick}>
      {breadcrumb.label}
    </StyledAncestorSpan>
  );
}

function renderTopLevelBreadcrumb(
  breadcrumb: FolderBreadcrumb,
  isSolitaryTopLevelBreadcrumb: boolean,
  style?: FolderBreadcrumbStyleOverride,
) {
  const StyledAncestorSpan = style?.ancestorSpan || StyledAncestorFolderBreadcrumbSpan;

  return [
    <StyledAncestorSpan
      $isSolitaryTopLevelBreadcrumb={isSolitaryTopLevelBreadcrumb}
      key="TopLevelBreadcrumb"
      onClick={breadcrumb.onBreadcrumbClick}
    >
      {breadcrumb.label}
    </StyledAncestorSpan>,
    style?.showSeparatorAfterTopLevel &&
      isSolitaryTopLevelBreadcrumb &&
      renderBreadcrumbSeparator(`BreadcrumbSeperator:top`, style?.breadcrumbSeparator),
  ];
}

function renderCurrentFolderBreadcrumb(
  breadcrumb: FolderBreadcrumb,
  currentFolderRef: RefObject<HTMLDivElement>,
  styles?: FolderBreadcrumbStyleOverride,
) {
  const StyledCurrentFolderSpan = styles?.currentFolderSpan || StyledCurrentFolderBreadcrumbSpan;

  return (
    <TooltipCoordinator key="CurrentFolderBreadcrumb" render={() => breadcrumb.label}>
      <TooltipOverflowCondition overflowRef={currentFolderRef}>
        <StyledCurrentFolderSpan onClick={breadcrumb.onBreadcrumbClick}>
          {breadcrumb.label}
        </StyledCurrentFolderSpan>
      </TooltipOverflowCondition>
    </TooltipCoordinator>
  );
}

function maybeRenderEllipsisBreadcrumb(
  breadcrumbs: ReadonlyArray<FolderBreadcrumb>,
  numBreadcrumbsToRender: number,
  renderBreadcrumbTooltipCallback: Renderer,
  onEllipsisBreadcrumbClick?: Handler | HandlerOf<FrontEvent>,
) {
  const shouldRenderSubfolderEllipsis = breadcrumbs.length > 1 && numBreadcrumbsToRender < breadcrumbs.length;
  if (!shouldRenderSubfolderEllipsis) {
    return null;
  }

  return (
    <StyledEllipsisTooltipCoordinator
      key="EllipsisBreadcrumb"
      render={renderBreadcrumbTooltipCallback}
      maxWidth={650}
    >
      <StyledEllpsisBreadcrumbSpan onClick={onEllipsisBreadcrumbClick}>...</StyledEllpsisBreadcrumbSpan>
    </StyledEllipsisTooltipCoordinator>
  );
}

function renderFolderPathBreadcrumbs(
  folderPathBreadcrumbsToRender: ReadonlyArray<FolderBreadcrumb>,
  currentFolderRef: RefObject<HTMLDivElement>,
  styles?: FolderBreadcrumbStyleOverride,
) {
  if (folderPathBreadcrumbsToRender.length === 0) {
    return [];
  }

  const ancestorBreadcrumbs = folderPathBreadcrumbsToRender.slice(0, -1);
  const currentFolderBreadcrumb = folderPathBreadcrumbsToRender[folderPathBreadcrumbsToRender.length - 1];

  // Index should be fine for the key as this isn't going to change.
  const ancestorNodes = ancestorBreadcrumbs.map((breadcrumb, index) =>
    renderAncestorBreadcrumb(breadcrumb, `Ancestor:${index}`, styles),
  );

  return [...ancestorNodes, renderCurrentFolderBreadcrumb(currentFolderBreadcrumb, currentFolderRef, styles)];
}

function renderBreadcrumbs(
  folderPathBreadcrumbs: ReadonlyArray<FolderBreadcrumb>,
  topLevelBreadcrumb: FolderBreadcrumb,
  numBreadcrumbsToRender: number,
  currentFolderRef: RefObject<HTMLDivElement>,
  renderAllBreadcrumbsTooltipCallback: Renderer,
  styles?: FolderBreadcrumbStyleOverride,
) {
  const folderPathBreadcrumbsToRender = folderPathBreadcrumbs.slice(-numBreadcrumbsToRender);

  // Clicking on the ellipsis will navigate to the first hidden ancestor.
  const firstHiddenAncestorIndex = folderPathBreadcrumbs.length - (numBreadcrumbsToRender + 1);
  const firstHiddenAncestor =
    firstHiddenAncestorIndex >= 0 ? folderPathBreadcrumbs[firstHiddenAncestorIndex] : undefined;

  const allBreadcrumbNodes: ReadonlyArray<ReactNode> = [
    renderTopLevelBreadcrumb(topLevelBreadcrumb, folderPathBreadcrumbsToRender.length === 0, styles),
    maybeRenderEllipsisBreadcrumb(
      folderPathBreadcrumbs,
      numBreadcrumbsToRender,
      renderAllBreadcrumbsTooltipCallback,
      firstHiddenAncestor?.onBreadcrumbClick,
    ),
    ...renderFolderPathBreadcrumbs(folderPathBreadcrumbsToRender, currentFolderRef, styles),
  ].filter((node) => Boolean(node));

  // Index should be fine for the key as this isn't going to change.
  return allBreadcrumbNodes
    .flatMap((breadcrumbNode: ReactNode, index: number) => [
      breadcrumbNode,
      renderBreadcrumbSeparator(`BreadcrumbSeperator:${index}`, styles?.breadcrumbSeparator),
    ])
    .slice(0, -1);
}

function renderBreadcrumbSeparator(key: string, breadcrumbSeparatorOverride?: ReactNode) {
  return breadcrumbSeparatorOverride ? (
    <Fragment key={key}>{breadcrumbSeparatorOverride}</Fragment>
  ) : (
    <StyledChevronIcon key={key} name="chevronRightTiny" />
  );
}

function renderAllBreadcrumbsTooltip(
  topLevelBreadcrumb: FolderBreadcrumb,
  folderPathBreadcrumbs: ReadonlyArray<FolderBreadcrumb>,
) {
  if (folderPathBreadcrumbs.length === 0) {
    return null;
  }

  const allBreadcrumbNodes: ReadonlyArray<ReactNode> = [
    topLevelBreadcrumb.label,
    ...folderPathBreadcrumbs.map((breadcrumb) => breadcrumb.label),
  ];

  // Index should be fine for the key as this isn't going to change.
  return allBreadcrumbNodes
    .flatMap((breadcrumbNode: ReactNode, index: number) => [
      <Fragment key={`breadcrumb:${index}`}>{breadcrumbNode}</Fragment>,
      <StyledChevronIcon key={`chevron:${index}`} name="chevronRightTiny" />,
    ])
    .slice(0, -1);
}

function isElementOverflowing(node: HTMLElement) {
  return node.scrollWidth > node.offsetWidth;
}
